import { useState } from "react";
import { ApiError } from "../../api/client";
import type { Authorization, Settlement } from "../../api/types";
import { useTill } from "../../app/till-context";
import { t } from "../../i18n";
import { formatTime } from "../../lib/time";
import { Dialog } from "../../ui/Dialog";

interface Props {
  settlement: Settlement;
  /** The manager or the owner who gave their code. */
  authorization: Authorization;
  onUndone: (settlement: Settlement) => void;
  onClose: () => void;
}

/**
 * "Afrekening ongedaan maken" (Furkan, 11/10/2026: a courier settled by mistake): with a reason; the
 * deliveries go back to the courier to settle again, the printed settlement stays.
 */
export function UndoSettlementDialog({ settlement, authorization, onUndone, onClose }: Props) {
  const { api } = useTill();
  const text = t.couriers.undoDialog;
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const undo = async () => {
    setBusy(true);
    setError(null);
    try {
      const answer = await api.request<{ settlement: Settlement }>(
        "POST",
        `/device/settlements/${settlement.id}/undo`,
        { body: { reason: reason.trim() }, staffToken: authorization.token },
      );
      onUndone(answer.settlement);
    } catch (failure) {
      setError(failure instanceof ApiError ? failure.message : t.errors.unexpected);
      setBusy(false);
    }
  };

  return (
    <Dialog
      title={text.title(settlement.courier.name, formatTime(settlement.settledAt))}
      onClose={onClose}
      footer={
        <>
          <span className="spacer" />
          <button type="button" className="btn" onClick={onClose}>
            {text.cancel}
          </button>
          <button
            type="button"
            className="btn danger"
            disabled={busy || reason.trim() === ""}
            onClick={() => void undo()}
          >
            {text.confirm}
          </button>
        </>
      }
    >
      <p className="note">{text.note}</p>
      <label className="field">
        <span>{text.reason}</span>
        <input
          value={reason}
          maxLength={200}
          placeholder={text.placeholder}
          data-autofocus
          onChange={(event) => {
            setReason(event.target.value);
          }}
        />
      </label>
      {error !== null && (
        <p className="error" role="alert">
          {error}
        </p>
      )}
    </Dialog>
  );
}
